import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Grid, Typography, Divider } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { CompanyDto, CompanyAddressDto, CompanyRegisteredImporterDto } from 'dtos'
import { useEffect, useState } from 'react'
import RegisteredImporterAddEditDialog from './RegisteredImporterAddEditDialog'
import { enqueueSnackbar } from 'notistack'
import { Chip } from 'components/chips'
import { useAuth } from 'context'

interface RegisteredImporterAddEditDialogProps {
  company: CompanyDto
}

export default function RegisteredImporterListCard({ company }: RegisteredImporterAddEditDialogProps) {
  const { permissions } = useAuth()

  const [isRegisteredImporterSaving, setIsRegisteredImporterSaving] = useState<boolean>(false)
  const [registeredImporterAddEditDialogOpen, setRegisteredImporterAddEditDialogOpen] = useState<boolean>(false)
  const [companyRegisteredImporter, setCompanyRegisteredImporter] = useState<CompanyRegisteredImporterDto>(
    new CompanyRegisteredImporterDto()
  )
  const [isGettingRegisteredImporter, setIsGettingRegisteredImporter] = useState<boolean>(false)
  const [isGettingRegisteredImporterAddress, setIsGettingRegisteredImporterAddress] = useState<boolean>(false)
  const [registeredImporterAddress, setRegisteredImporterAddress] = useState<CompanyAddressDto | undefined>(undefined)
  const [needsEmail, setNeedsEmail] = useState<boolean>(false)
  const [needsPhone, setNeedsPhone] = useState<boolean>(false)

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingRegisteredImporter(true)
      controllers
        .getCompanyRegisteredImporter(company.companyGuid)
        .then(response => {
          setCompanyRegisteredImporter(response.value)
        })
        .finally(() => {
          setIsGettingRegisteredImporter(false)
        })
    }
  }, [company])

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingRegisteredImporterAddress(true)
      controllers
        .getRegisteredImporterAddress()
        .then(response => {
          const address = response.value
          setRegisteredImporterAddress(address)
          setNeedsEmail(!address?.email)
          setNeedsPhone(!address?.phone)
        })
        .finally(() => {
          setIsGettingRegisteredImporterAddress(false)
        })
    }
  }, [company.companyGuid])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <RegisteredImporterAddEditDialog
          initialValues={companyRegisteredImporter}
          loading={isRegisteredImporterSaving}
          onClose={() => {
            setRegisteredImporterAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsRegisteredImporterSaving(true)
            controllers
              .updateCompanyRegisteredImporter(company.companyGuid!, values)
              .then(response => {
                enqueueSnackbar('Registered Importer Information Saved Successfully!', { variant: 'success' })
                setCompanyRegisteredImporter(response.value)
                setRegisteredImporterAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsRegisteredImporterSaving(false)
              })
          }}
          open={registeredImporterAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between' spacing={2}>
              <Grid item container alignItems='center' spacing={1} xs>
                <Grid item xs={12} sm='auto'>
                  <Typography fontWeight='bold' variant='h5'>
                    Registered Importer
                  </Typography>
                </Grid>
                {!registeredImporterAddress && !isGettingRegisteredImporterAddress && (
                  <Grid item xs={12} sm='auto'>
                    <Chip variant='red'>Need Address</Chip>
                  </Grid>
                )}
                {needsEmail && (
                  <Grid item xs={12} sm='auto'>
                    <Chip variant='red'>Needs Email</Chip>
                  </Grid>
                )}
                {needsPhone && (
                  <Grid item xs={12} sm='auto'>
                    <Chip variant='red'>Needs Phone</Chip>
                  </Grid>
                )}
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setIsRegisteredImporterSaving(false)
                      setRegisteredImporterAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingRegisteredImporter && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingRegisteredImporter && (
              <>
                <Grid item xs={12}>
                  <LabelField label='Importer #' value={companyRegisteredImporter.registeredImporterNumber} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='NHTSA Registration #'
                    value={companyRegisteredImporter.registeredImporterNhtsaRegistrationNumber}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Registered Importer IRS #' value={companyRegisteredImporter.registeredImporterIrsNumber} />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                ||This card only shows if the company is marked as a Registered Importer.|| If there is no address, email and/or
                phone number the card will indicate it via red text. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
